import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';

function UpdateAssistance() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    needsAssistance: '',
    walkingAssistance: '',
    fulltimeCaretaker: '',
    consultation: '',
    dailyGuidance: '',
    signLanguageInterp: '',
    parttimeCaretaker: '',
  });

  const assistanceTypes = [
    { name: 'walkingAssistance', label: "A person's assistance in walking" },
    { name: 'fulltimeCaretaker', label: 'Full-time caretaker' },
    { name: 'consultation', label: 'Consultation' },
    { name: 'dailyGuidance', label: 'Guidance for daily activities' },
    { name: 'signLanguageInterp', label: 'Sign Language Interpretations' },
    { name: 'parttimeCaretaker', label: 'Part-time caretaker' },
  ];

  const availabilityOptions = [
    'Currently available',
    'Required',
    'Not required',
  ];

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message: 'Missing required parameters.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getAssistanceDetails(
        recordId,
        sheetId
      );

      if (!response.success) {
        setAlert({
          show: true,
          message: response.message || 'Failed to load assistance details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        needsAssistance: response.record.needsAssistance || '',
        walkingAssistance: response.record.walkingAssistance || '',
        fulltimeCaretaker: response.record.fulltimeCaretaker || '',
        consultation: response.record.consultation || '',
        dailyGuidance: response.record.dailyGuidance || '',
        signLanguageInterp: response.record.signLanguageInterp || '',
        parttimeCaretaker: response.record.parttimeCaretaker || '',
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updates = { [name]: value };

    // If needs assistance is set to No, clear all assistance types
    if (name === 'needsAssistance' && value === 'No') {
      updates = {
        ...updates,
        walkingAssistance: '',
        fulltimeCaretaker: '',
        consultation: '',
        dailyGuidance: '',
        signLanguageInterp: '',
        parttimeCaretaker: '',
      };
    }

    setFormData({
      ...formData,
      ...updates,
    });
  };

  const shouldShowAssistanceDetails = () => {
    return formData.needsAssistance === 'Yes';
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.needsAssistance) {
      setAlert({
        show: true,
        message: 'Please select whether you need assistance from others.',
        type: 'warning',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      const updateData = {
        uniqueId: recordData.uniqueId,
        needsAssistance: formData.needsAssistance,
        walkingAssistance:
          formData.needsAssistance === 'Yes' ? formData.walkingAssistance : '',
        fulltimeCaretaker:
          formData.needsAssistance === 'Yes' ? formData.fulltimeCaretaker : '',
        consultation:
          formData.needsAssistance === 'Yes' ? formData.consultation : '',
        dailyGuidance:
          formData.needsAssistance === 'Yes' ? formData.dailyGuidance : '',
        signLanguageInterp:
          formData.needsAssistance === 'Yes' ? formData.signLanguageInterp : '',
        parttimeCaretaker:
          formData.needsAssistance === 'Yes' ? formData.parttimeCaretaker : '',
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateAssistanceDetails(updateData);

      if (response.success) {
        setAlert({
          show: true,
          message: 'Assistance details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/home/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/economic/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={9}
        title="Update Assistance from Others"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading assistance details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={9}
        title="Update Assistance from Others"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load assistance details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={9}
      title="Update Assistance from Others"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">Assistance Requirements (Editable)</div>

        <Form.Group className="mb-3">
          <Form.Label>
            Do you need assistance from others?{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Yes"
              name="needsAssistance"
              value="Yes"
              checked={formData.needsAssistance === 'Yes'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="No"
              name="needsAssistance"
              value="No"
              checked={formData.needsAssistance === 'No'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {/* Assistance Details */}
        {shouldShowAssistanceDetails() && (
          <div className="conditional-field">
            {assistanceTypes.map((assistance, index) => (
              <div key={index} className="mb-4 assistance-group">
                <Form.Label className="fw-bold">{assistance.label}</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {availabilityOptions.map((option, optIndex) => (
                    <Form.Check
                      key={optIndex}
                      inline
                      type="radio"
                      label={option}
                      name={assistance.name}
                      value={option}
                      checked={formData[assistance.name] === option}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Form>
    </UpdateLayout>
  );
}

export default UpdateAssistance;
